import React, { useEffect, useMemo, useState, useCallback } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import {
  FaMapMarkedAlt, FaPlus, FaSyncAlt, FaArrowRight, FaArrowLeft, FaCheck,
  FaBullhorn, FaTrashAlt, FaInbox, FaExclamationTriangle, FaRoute,
} from 'react-icons/fa';
import {
  TechShell, TechPageHeader, TechSection, TechInput, TechSelect,
  TechTextarea, TechButton, TechButtonGhost, StatStrip, StatTile, StatLabel,
  StatValue, StatSub, StatusPill, TechChip, Empty, Loading, ErrorBox,
  borderSoft, accent, mono,
} from '../../styles/techComponents';
import {
  listCasework, createCasework, updateCasework, deleteCasework,
  makeContentFromCasework, listBarrios, getCoverage, listVisits, createVisit,
} from '../../services/territorioService';
import MapaBarrios from './MapaBarrios';
import BarrioDetalle from './BarrioDetalle';

const ESTADOS = [
  { id: 'detectado', label: 'Detectado', color: '#f59e0b' },
  { id: 'tomado', label: 'Tomado', color: '#00b4d8' },
  { id: 'en_gestion', label: 'En gestión', color: '#8b5cf6' },
  { id: 'resuelto', label: 'Resuelto', color: '#22c55e' },
];
const TIPOS = ['bache', 'luminaria', 'seguridad', 'limpieza', 'salud', 'espacios_verdes', 'transito', 'otro'];
const PRIOS = { alta: '#ef4444', media: '#f59e0b', baja: '#64748b' };
const nextEstado = (e) => ESTADOS[Math.min(ESTADOS.findIndex((x) => x.id === e) + 1, ESTADOS.length - 1)].id;
const prevEstado = (e) => ESTADOS[Math.max(ESTADOS.findIndex((x) => x.id === e) - 1, 0)].id;

const Board = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const Col = styled.div`
  background: rgba(11, 19, 32, 0.4);
  border: 1px solid ${borderSoft};
  border-top: 2px solid ${({ $c }) => $c};
  border-radius: 8px;
  padding: 0.7rem;
  min-height: 120px;
`;
const ColHead = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  font-family: ${mono}; font-size: 0.72rem; letter-spacing: 0.08em; text-transform: uppercase;
  color: ${({ $c }) => $c}; margin-bottom: 0.6rem;
`;
const CWCard = styled.article`
  background: var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${({ $p }) => PRIOS[$p] || accent};
  border-radius: 6px; padding: 0.65rem 0.7rem; margin-bottom: 0.55rem;
`;
const CWTitle = styled.h4`margin: 0 0 0.3rem; font-size: 0.9rem; line-height: 1.3;`;
const CWMeta = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.3rem; margin: 0.35rem 0;
  font-family: ${mono}; font-size: 0.66rem; color: var(--text-secondary);
`;
const CWActions = styled.div`display: flex; gap: 0.35rem; flex-wrap: wrap; margin-top: 0.45rem;`;
const MiniBtn = styled.button`
  display: inline-flex; align-items: center; gap: 0.25rem;
  background: rgba(255,255,255,0.04); color: var(--text-secondary);
  border: 1px solid ${borderSoft}; border-radius: 5px; padding: 0.2rem 0.45rem;
  font-size: 0.68rem; cursor: pointer; font-family: ${mono};
  &:hover { color: var(--text-primary); border-color: ${accent}; }
`;
const Form = styled.div`
  display: grid; grid-template-columns: 2fr 1fr 1fr 1fr auto; gap: 0.5rem; align-items: end;
  @media (max-width: 900px) { grid-template-columns: 1fr 1fr; }
`;
const CovGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 0.6rem;`;
const CovCard = styled.div`
  border: 1px solid ${borderSoft}; border-radius: 7px; padding: 0.7rem 0.8rem;
  background: rgba(11,19,32,0.4);
  h5 { margin: 0 0 0.4rem; font-size: 0.9rem; }
  .row { display: flex; justify-content: space-between; font-family: ${mono}; font-size: 0.7rem; color: var(--text-secondary); }
`;
const VisitForm = styled.div`
  display: grid; grid-template-columns: 1.2fr 0.8fr 2fr auto; gap: 0.5rem; align-items: end;
  @media (max-width: 900px) { grid-template-columns: 1fr 1fr; }
`;
const VisitList = styled.div`display: flex; flex-direction: column; gap: 0.5rem; margin-top: 1rem;`;
const VisitRow = styled.div`
  display: flex; align-items: baseline; gap: 0.6rem; flex-wrap: wrap;
  border: 1px solid ${borderSoft}; border-left: 3px solid ${accent};
  border-radius: 6px; padding: 0.55rem 0.7rem; background: rgba(11,19,32,0.4);
  h5 { margin: 0; font-size: 0.88rem; }
  .fecha { font-family: ${mono}; font-size: 0.68rem; color: ${accent}; }
  .notas { flex: 1 1 100%; font-size: 0.82rem; color: var(--text-secondary); margin-top: 0.2rem; }
`;

const hoy = () => new Date().toISOString().slice(0, 10);

export default function Territorio() {
  const [items, setItems] = useState([]);
  const [barrios, setBarrios] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [barrioSel, setBarrioSel] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ titulo: '', tipo: 'bache', barrio: '', prioridad: 'media' });
  const [visitForm, setVisitForm] = useState({ barrio_id: '', fecha: hoy(), notas: '' });
  const [savingVisit, setSavingVisit] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [cw, bs, cov, vs] = await Promise.all([listCasework(), listBarrios(), getCoverage(), listVisits()]);
      setItems(cw); setBarrios(bs); setCoverage(cov); setVisits(vs);
    } catch (e) {
      setError(friendlyError(e, 'No se pudo cargar la gestión territorial.'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 2500); };

  const byEstado = useMemo(() => {
    const g = { detectado: [], tomado: [], en_gestion: [], resuelto: [] };
    for (const it of items) if (g[it.estado]) g[it.estado].push(it);
    return g;
  }, [items]);

  const totals = coverage?.totals || {};

  async function move(it, estado) {
    try {
      const upd = await updateCasework(it.id, { estado });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, ...upd } : x)));
      if (estado === 'resuelto') flash('Reclamo resuelto. Podés generar un posteo desde la tarjeta.');
    } catch (e) { flash(e?.response?.data?.error || 'No se pudo mover'); }
  }
  async function remove(it) {
    try { await deleteCasework(it.id); setItems((prev) => prev.filter((x) => x.id !== it.id)); }
    catch (e) { flash('No se pudo eliminar'); }
  }
  async function genContent(it) {
    try { await makeContentFromCasework(it.id, { channel: 'social' }); flash('Posteo generado → Bandeja de Mensajería (pendiente de aprobación).'); }
    catch (e) { flash(e?.response?.data?.error || 'No se pudo generar'); }
  }
  async function addCasework() {
    if (!form.titulo.trim()) return flash('Poné un título');
    try {
      const cw = await createCasework({ ...form, estado: 'tomado' });
      setItems((prev) => [cw, ...prev]);
      setForm({ titulo: '', tipo: 'bache', barrio: '', prioridad: 'media' });
      flash('Reclamo cargado');
    } catch (e) { flash(e?.response?.data?.error || 'No se pudo crear'); }
  }
  async function registerVisit() {
    if (!visitForm.barrio_id) return flash('Elegí un barrio');
    setSavingVisit(true);
    try {
      await createVisit({
        barrio_id: Number(visitForm.barrio_id),
        fecha: visitForm.fecha || undefined,
        notas: visitForm.notas.trim() || undefined,
      });
      // refrescamos listado + cobertura para que dias_sin_visita se actualice
      const [vs, cov] = await Promise.all([listVisits(), getCoverage()]);
      setVisits(vs); setCoverage(cov);
      setVisitForm({ barrio_id: '', fecha: hoy(), notas: '' });
      flash('Recorrida registrada');
    } catch (e) {
      flash(e?.response?.data?.error || 'No se pudo registrar la recorrida');
    } finally { setSavingVisit(false); }
  }

  if (loading) return <TechShell><Loading>Cargando gestión territorial…</Loading></TechShell>;

  return (
    <TechShell>
      <TechPageHeader
        title="Gestión Territorial"
        dossier="TERRITORIO · SN"
        subtitle="Reclamos de vecinos, gestión resuelta y cobertura por barrio. El motor detecta problemas de las noticias y vos los resolvés y los convertís en contenido."
        actions={<TechButtonGhost onClick={load}><FaSyncAlt /> Actualizar</TechButtonGhost>}
      />

      <TechSection title="Mapa de San Nicolás — barrios y cobertura" icon={<FaMapMarkedAlt />}>
        <MapaBarrios barrios={coverage?.barrios || []} onCoordsSaved={load} onSelectBarrio={setBarrioSel} />
      </TechSection>

      {barrioSel && <BarrioDetalle barrio={barrioSel} onClose={() => setBarrioSel(null)} />}

      <StatStrip>
        <StatTile><StatLabel>Detectados</StatLabel><StatValue>{totals.detectados ?? 0}</StatValue><StatSub>en cola para tomar</StatSub></StatTile>
        <StatTile><StatLabel>En curso</StatLabel><StatValue>{totals.en_curso ?? 0}</StatValue><StatSub>tomados + en gestión</StatSub></StatTile>
        <StatTile><StatLabel>Resueltos (7d)</StatLabel><StatValue>{totals.resueltos_semana ?? 0}</StatValue><StatSub>de {totals.resueltos ?? 0} totales</StatSub></StatTile>
        <StatTile><StatLabel>Barrios visitados</StatLabel><StatValue>{totals.barrios_visitados_30d ?? 0}</StatValue><StatSub>de {totals.barrios_total ?? 0} (30 días)</StatSub></StatTile>
      </StatStrip>

      {error && <ErrorBox><FaExclamationTriangle /> {error}</ErrorBox>}
      {toast && <StatusPill style={{ marginBottom: '0.6rem' }}>{toast}</StatusPill>}

      <TechSection title="Cargar reclamo" icon={<FaPlus />}>
            <Form>
              <div><StatLabel>Título</StatLabel><TechInput placeholder="Ej: Bache profundo en Ruta 188 y..." value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} /></div>
              <div><StatLabel>Tipo</StatLabel><TechSelect value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>{TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}</TechSelect></div>
              <div><StatLabel>Barrio</StatLabel><TechInput list="barrios-dl" placeholder="Barrio/zona" value={form.barrio} onChange={(e) => setForm({ ...form, barrio: e.target.value })} /><datalist id="barrios-dl">{barrios.map((b) => <option key={b.id} value={b.nombre} />)}</datalist></div>
              <div><StatLabel>Prioridad</StatLabel><TechSelect value={form.prioridad} onChange={(e) => setForm({ ...form, prioridad: e.target.value })}><option value="alta">alta</option><option value="media">media</option><option value="baja">baja</option></TechSelect></div>
              <TechButton onClick={addCasework}><FaPlus /> Cargar</TechButton>
            </Form>
          </TechSection>

          <Board>
            {ESTADOS.map((col) => (
              <Col key={col.id} $c={col.color}>
                <ColHead $c={col.color}>{col.label}<span>{byEstado[col.id].length}</span></ColHead>
                {byEstado[col.id].length === 0 && <Empty style={{ padding: '0.6rem', fontSize: '0.75rem' }}>—</Empty>}
                {byEstado[col.id].map((it) => (
                  <CWCard key={it.id} $p={it.prioridad}>
                    <CWTitle>{it.titulo}</CWTitle>
                    <CWMeta>
                      {it.tipo && <TechChip>{it.tipo}</TechChip>}
                      {it.barrio_nombre && <span>📍 {it.barrio_nombre}</span>}
                      {it.origen !== 'manual' && <span>🤖 {it.origen}</span>}
                    </CWMeta>
                    <CWActions>
                      {it.estado !== 'detectado' && <MiniBtn onClick={() => move(it, prevEstado(it.estado))}><FaArrowLeft /></MiniBtn>}
                      {it.estado !== 'resuelto' && <MiniBtn onClick={() => move(it, nextEstado(it.estado))}><FaArrowRight /> {ESTADOS[ESTADOS.findIndex((x) => x.id === it.estado) + 1]?.label}</MiniBtn>}
                      {it.estado === 'resuelto' && <MiniBtn onClick={() => genContent(it)}><FaBullhorn /> Posteo</MiniBtn>}
                      {it.fuente_url && <MiniBtn as="a" href={it.fuente_url} target="_blank" rel="noreferrer">fuente</MiniBtn>}
                      <MiniBtn onClick={() => remove(it)}><FaTrashAlt /></MiniBtn>
                    </CWActions>
                  </CWCard>
                ))}
              </Col>
            ))}
          </Board>

      <TechSection title={`Cola de detectados (${byEstado.detectado.length}) — encontrados por la IA`} icon={<FaInbox />}>
          {byEstado.detectado.length === 0 && <Empty>Sin reclamos detectados pendientes. El cron los busca en las noticias cada día.</Empty>}
          {byEstado.detectado.map((it) => (
            <CWCard key={it.id} $p={it.prioridad}>
              <CWTitle>{it.titulo}</CWTitle>
              {it.descripcion && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{it.descripcion}</div>}
              <CWMeta>{it.tipo && <TechChip>{it.tipo}</TechChip>}{it.barrio_nombre && <span>📍 {it.barrio_nombre}</span>}<span>prioridad: {it.prioridad}</span></CWMeta>
              <CWActions>
                <MiniBtn onClick={() => move(it, 'tomado')}><FaCheck /> Tomar</MiniBtn>
                {it.fuente_url && <MiniBtn as="a" href={it.fuente_url} target="_blank" rel="noreferrer">ver fuente</MiniBtn>}
                <MiniBtn onClick={() => move(it, 'descartado')}>descartar</MiniBtn>
              </CWActions>
            </CWCard>
          ))}
        </TechSection>

      <TechSection title="Cobertura por barrio" icon={<FaMapMarkedAlt />}>
          <CovGrid>
            {(coverage?.barrios || []).map((b) => (
              <CovCard key={b.id} onClick={() => setBarrioSel(b)} style={{ cursor: 'pointer' }}>
                <h5>{b.nombre}</h5>
                <div className="row"><span>Abiertos</span><b>{b.reclamos_abiertos}</b></div>
                <div className="row"><span>Resueltos</span><b>{b.reclamos_resueltos}</b></div>
                <div className="row"><span>Última visita</span><b>{b.dias_sin_visita == null ? 'nunca' : `hace ${b.dias_sin_visita}d`}</b></div>
              </CovCard>
            ))}
          </CovGrid>
        </TechSection>

      <TechSection title="Registrar recorrida" icon={<FaRoute />}>
            <VisitForm>
              <div>
                <StatLabel>Barrio</StatLabel>
                <TechSelect value={visitForm.barrio_id} onChange={(e) => setVisitForm({ ...visitForm, barrio_id: e.target.value })}>
                  <option value="">Elegí un barrio…</option>
                  {barrios.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </TechSelect>
              </div>
              <div>
                <StatLabel>Fecha</StatLabel>
                <TechInput type="date" value={visitForm.fecha} onChange={(e) => setVisitForm({ ...visitForm, fecha: e.target.value })} />
              </div>
              <div>
                <StatLabel>Notas</StatLabel>
                <TechTextarea placeholder="Qué viste, con quién hablaste, qué prometiste…" value={visitForm.notas} onChange={(e) => setVisitForm({ ...visitForm, notas: e.target.value })} />
              </div>
              <TechButton onClick={registerVisit} disabled={savingVisit}><FaPlus /> {savingVisit ? 'Registrando…' : 'Registrar recorrida'}</TechButton>
            </VisitForm>
          </TechSection>

          <TechSection title={`Últimas recorridas (${visits.length})`} icon={<FaRoute />}>
            {visits.length === 0 && <Empty>Todavía no cargaste recorridas. Registrá una arriba para empezar a medir la cobertura por barrio.</Empty>}
            <VisitList>
              {visits.map((v) => (
                <VisitRow key={v.id}>
                  <h5>{v.barrio_nombre || 'Barrio sin nombre'}</h5>
                  <span className="fecha">{(v.fecha || '').slice(0, 10)}</span>
                  {v.notas && <div className="notas">{v.notas}</div>}
                </VisitRow>
              ))}
            </VisitList>
          </TechSection>
    </TechShell>
  );
}
